import React, { MouseEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalLayout } from 'ui/ModalLayout/ModalLayout'
import RestrictionsService from '../../../../parts/services/RestrictionsService'
import {
	onChangeTaskInput,
	onChangeTextInput,
	useGetGeneratedPromptAndSetToStore,
	useGetGeneratedTextAndSetToStore,
	useGetNewValueInputValue,
} from './fn/changeForm'
import { TextExerciseFormStateType } from './fn/formState'
import ExerciseFormFooter from '../common/ExerciseFormFooter/ExerciseFormFooter'
import HtmlEditor from 'ui/HTMLEditor/HTMLEditor'
import TrainingEntityTypes from 'parts/types/TrainingEntityTypes'
import useExercisesListAdminStore from '../../zustand/store'
import Generator from '../Generator/Generator'
import { useSetFormData } from './fn/setFormData'
import { submitHandler } from './fn/submit'
import './TextForm.scss'

type VideoFormProps = {
	saveExercise: (args: TrainingEntityTypes.ExerciseItem) => void // Функция сохраняющая данные упражнения на сервере и в Хранилище
	closeModalFn: MouseEventHandler<HTMLElement> // Функция закрытия модального окна
}

// Форма составления видео-упражнения
function TextForm(props: VideoFormProps) {
	const { saveExercise, closeModalFn } = props

	// Вычленить данные упражнения из данных курса, преобразовать в данные состояния
	// для редактирования и поставить в Хранилище
	useSetFormData()

	const canUseAiGeneration = RestrictionsService.useCanUseAiGeneration()

	const { t } = useTranslation()
	// Функция получающая сгенерированный текст и помещающая его в поле с текстом упражнения
	const getGeneratedText = useGetGeneratedTextAndSetToStore()
	// Возвращает функцию получающую текст запроса на генерирование текста.
	// Эта информация сохраняется в данных упражнения
	const getGeneratedPrompt = useGetGeneratedPromptAndSetToStore()

	// Состояние упражнения
	const formState = useExercisesListAdminStore(
		(store) => store.formData
	) as TextExerciseFormStateType
	if (!formState) return null

	return (
		<ModalLayout
			key="1"
			title="Добавление текста"
			cancelBtn={false}
			isModalVisible
			closeModalFn={closeModalFn}
			footer={null}
		>
			<TaskInput formState={formState} />
			<TextInput formState={formState} />
			{canUseAiGeneration && (
				<Generator
					exerciseType="text"
					getGeneratedText={getGeneratedText}
					initialPrompt={formState.generatedTextPrompt}
					getGeneratedPrompt={getGeneratedPrompt}
					error={null}
				/>
			)}
			<ExerciseFormFooter
				formState={formState}
				saveExercise={saveExercise}
				closeModalFn={closeModalFn}
				isSubmitButtonDisabled={formState.isFormInvalid}
				submitHandler={() => submitHandler(saveExercise)}
			/>
		</ModalLayout>
	)
}

export default TextForm

type ExerciseTaskProps = {
	formState: TextExerciseFormStateType
}

// Поле с текстом задания
function TaskInput(props: ExerciseTaskProps) {
	const { formState } = props

	const { t } = useTranslation()

	return (
		<>
			<label className="exercise-text__label">Задание</label>
			<HtmlEditor
				initialValue={formState.taskInput.value}
				onChangeValue={onChangeTaskInput}
			/>
		</>
	)
}

// Поле с текстом задания
function TextInput(props: ExerciseTaskProps) {
	const { formState } = props

	const { t } = useTranslation()
	const newValue = useGetNewValueInputValue()

	return (
		<>
			<label className="exercise-text__label exercise-text__label--top-margin">
				Текст
			</label>
			<HtmlEditor
				initialValue={formState.textInput.value}
				newValue={newValue}
				onChangeValue={onChangeTextInput}
			/>
			{formState.textInput.error && (
				<p className="exercise-text__input-error">
					{formState.textInput.error}
				</p>
			)}
		</>
	)
}
